import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Badge,
  Box,
  Button,
  Group,
  NumberInput,
  Select,
  Stack,
  Switch,
  Table,
  Text,
  TextInput,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { z } from 'zod';
import { ANCHOR_TYPES, DONOR_STATUSES, type DonorStatus } from '@seo/contracts';
import { CreatePanel } from '../components/CreatePanel';
import { DataTable, type Column } from '../components/DataTable';
import { ExportButton } from '../components/ExportButton';
import { RowEditor } from '../components/RowEditor';
import { GuestPostEditor } from './GuestPostEditor';
import { publicMessageOf, useTRPC } from '../lib/trpc';
import { zodResolver } from '../lib/zod-resolver';
import classes from './EntityPage.module.css';

const AUDIT_COLOR: Record<string, string> = {
  FOUND_OK: 'mint',
  FOUND_TOO_SHORT: 'amber',
  FOUND_NO_ANCHOR: 'amber',
  NOT_FOUND: 'rose',
};

const DONOR_STATUS_COLOR: Record<string, string> = {
  new: 'gray',
  approved: 'mint',
  rejected: 'amber',
  blacklist: 'rose',
};

interface DonorRow {
  id: string;
  domain: string;
  dr: number | null;
  traffic: number | null;
  price: number | null;
  priceCurrency: string;
  contact: string | null;
  gamblingAllowed: boolean;
  spamSignalsPct: number | null;
  status: DonorStatus;
}

/** Числові поля форми лишаються рядками — валідуються тими самими межами, що на сервері. */
const optionalIntField = (min: number, max: number, message: string) =>
  z.string().refine((raw) => {
    const value = raw.trim();
    if (value === '') return true;
    return /^\d+$/.test(value) && Number(value) >= min && Number(value) <= max;
  }, message);

const donorSchema = z.object({
  domain: z.string().trim().min(3, 'Вкажіть домен донора').max(253),
  dr: optionalIntField(0, 100, 'DR — число від 0 до 100'),
  traffic: optionalIntField(0, 1_000_000_000, 'Трафік — невідʼємне число'),
  price: optionalIntField(0, 1_000_000, 'Ціна — невідʼємне число'),
  priceCurrency: z.string().trim().length(3, 'Код валюти — 3 літери (напр. USD)'),
  contact: z.string().max(300),
  gamblingAllowed: z.boolean(),
  status: z.enum(DONOR_STATUSES),
});
type DonorFormValues = z.infer<typeof donorSchema>;

const donorColumns: Column<DonorRow>[] = [
  { key: 'domain', header: 'Домен', render: (row) => <span className={classes.mono}>{row.domain}</span> },
  {
    key: 'dr',
    header: 'DR',
    align: 'right',
    render: (row) => <span className={classes.mono}>{row.dr ?? '—'}</span>,
  },
  {
    key: 'traffic',
    header: 'Трафік',
    align: 'right',
    render: (row) => <span className={classes.mono}>{row.traffic ?? '—'}</span>,
  },
  {
    key: 'price',
    header: 'Ціна',
    align: 'right',
    render: (row) => (
      <span className={classes.mono}>
        {row.price === null ? '—' : `${String(row.price / 100)} ${row.priceCurrency}`}
      </span>
    ),
  },
  {
    key: 'contact',
    header: 'Контакт',
    render: (row) => <span className={classes.dim}>{row.contact ?? '—'}</span>,
  },
  {
    key: 'gambling',
    header: 'Гемблінг',
    render: (row) => (
      <Text className={row.gamblingAllowed ? classes.mono : classes.dim}>
        {row.gamblingAllowed ? 'так' : 'ні'}
      </Text>
    ),
  },
  {
    key: 'status',
    header: 'Статус',
    render: (row) => (
      <Badge size="sm" color={DONOR_STATUS_COLOR[row.status] ?? 'gray'} data-testid={`donor-status-${row.domain}`}>
        {row.status}
      </Badge>
    ),
  },
];

/**
 * Лінкбілдинг (§8), acceptance scenario 13.
 *
 * The plan, the anchors and the posts are on one screen because they are one decision: how
 * many posts, carrying which anchors, and how many of those actually survived on the donor.
 * Splitting them across three screens is how an anchor profile drifts for a quarter before
 * anybody notices.
 */
export function LinksPage(): React.JSX.Element {
  const trpc = useTRPC();
  const queryClient = useQueryClient();

  const [siteId, setSiteId] = useState<string | null>(null);
  const [budget, setBudget] = useState<number>(20);
  const [anchorPct, setAnchorPct] = useState<number>(40);
  /** Відкритий гест-пост: редактор живе панеллю під таблицею, як решта карток. */
  const [openPostId, setOpenPostId] = useState<string | null>(null);
  const [profileId, setProfileId] = useState<string | null>(null);
  const [anchorText, setAnchorText] = useState('');
  const [anchorType, setAnchorType] = useState<string>('exact');
  const [topicCount, setTopicCount] = useState<number>(5);
  /** Донори — реєстр, незалежний від вибраного сайту (§8). */
  const [donorStatusFilter, setDonorStatusFilter] = useState<DonorStatus | null>(null);
  const [donorCreateOpen, setDonorCreateOpen] = useState(false);
  const [editingDonor, setEditingDonor] = useState<DonorRow | null>(null);

  const sites = useQuery(
    trpc.sites.list.queryOptions({ pagination: { limit: 200, direction: 'desc' } }),
  );
  const donors = useQuery(
    trpc.links.donors.queryOptions(donorStatusFilter === null ? {} : { status: donorStatusFilter }),
  );
  const profiles = useQuery(
    trpc.prompts.list.queryOptions({ pagination: { limit: 100, direction: 'desc' } }),
  );
  const plans = useQuery({
    ...trpc.links.plans.queryOptions({ siteId: siteId ?? '' }),
    enabled: siteId !== null,
  });
  const anchors = useQuery({
    ...trpc.links.anchors.queryOptions({ ownerType: 'site', ownerId: siteId ?? '' }),
    enabled: siteId !== null,
  });
  const posts = useQuery({
    ...trpc.links.guestPosts.queryOptions({ siteId: siteId ?? '' }),
    enabled: siteId !== null,
  });
  const anchorList = useQuery({
    ...trpc.links.anchorList.queryOptions({ ownerType: 'site', ownerId: siteId ?? '' }),
    enabled: siteId !== null,
  });

  const invalidate = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: trpc.links.pathKey() });
  };

  const savePlan = useMutation(trpc.links.upsertPlan.mutationOptions({ onSuccess: invalidate }));
  const addAnchor = useMutation(
    trpc.links.addAnchor.mutationOptions({
      onSuccess: async () => {
        setAnchorText('');
        await invalidate();
      },
    }),
  );
  const generateTopics = useMutation(trpc.links.generateTopics.mutationOptions({ onSuccess: invalidate }));
  const generatePost = useMutation(trpc.links.generatePost.mutationOptions({ onSuccess: invalidate }));
  const rerunAudit = useMutation(trpc.links.rerunAudit.mutationOptions({ onSuccess: invalidate }));
  const assignAnchor = useMutation(trpc.links.assignAnchor.mutationOptions({ onSuccess: invalidate }));

  const donorForm = useForm<DonorFormValues>({
    initialValues: {
      domain: '',
      dr: '',
      traffic: '',
      price: '',
      priceCurrency: 'USD',
      contact: '',
      gamblingAllowed: false,
      status: 'new',
    },
    validate: zodResolver(donorSchema),
  });
  const createDonor = useMutation(
    trpc.links.upsertDonor.mutationOptions({
      onSuccess: async () => {
        donorForm.reset();
        setDonorCreateOpen(false);
        await invalidate();
      },
    }),
  );

  const plan = plans.data?.[0];
  const anchored = plan === undefined ? 0 : Math.round((plan.budgetPosts * plan.anchorPct) / 100);
  /** Скільки постів фактично вийшло анкорними — порівняння проти плану, а не лише сам план. */
  const actualAnchored = (posts.data ?? []).filter((post) => post.anchorKind === 'anchor').length;

  return (
    <Stack gap="md">
      <Group gap="md" align="flex-end">
        <Select
          label="Сайт"
          placeholder="домен"
          data={(sites.data?.items ?? []).map((site) => ({ value: site.id, label: site.domain }))}
          value={siteId}
          onChange={setSiteId}
          searchable
          data-testid="links-site"
          style={{ width: 260 }}
        />
      </Group>

      {/*
       * Донори — реєстр, а не поле форми: `links.donors` і `links.upsertDonor` існували в API
       * без жодного викликача, тож донор заводився тільки прямим записом у базу. Тут, а не під
       * вибраним сайтом, бо один донор здає посилання на будь-який сайт мережі — прив'язки до
       * конкретного домену в нього немає.
       */}
      <Box className={classes.panel} data-testid="links-donors">
        <Group justify="space-between" mb={8}>
          <Text className={classes.panelTitle}>Донори</Text>
          <Select
            size="xs"
            placeholder="усі статуси"
            clearable
            data={[...DONOR_STATUSES]}
            value={donorStatusFilter}
            onChange={setDonorStatusFilter}
            data-testid="donor-status-filter"
            style={{ width: 160 }}
          />
        </Group>

        <CreatePanel
          onReset={() => {
            createDonor.reset();
            donorForm.reset();
          }}
          zone="linkbuilding"
          label="Додати донора"
          testId="donor"
          open={donorCreateOpen}
          onOpenChange={setDonorCreateOpen}
          busy={createDonor.isPending}
          error={createDonor.error}
          onSubmit={donorForm.onSubmit((values) => {
            createDonor.mutate({
              domain: values.domain.trim(),
              dr: values.dr.trim() === '' ? null : Number(values.dr),
              traffic: values.traffic.trim() === '' ? null : Number(values.traffic),
              price: values.price.trim() === '' ? null : Math.round(Number(values.price) * 100),
              priceCurrency: values.priceCurrency.trim() || 'USD',
              contact: values.contact.trim() === '' ? null : values.contact.trim(),
              gamblingAllowed: values.gamblingAllowed,
              status: values.status,
            });
          })}
        >
          <TextInput
            size="xs"
            placeholder="домен донора"
            data-testid="donor-domain"
            style={{ width: 180 }}
            {...donorForm.getInputProps('domain')}
          />
          <TextInput
            size="xs"
            placeholder="DR"
            data-testid="donor-dr"
            style={{ width: 70 }}
            {...donorForm.getInputProps('dr')}
          />
          <TextInput
            size="xs"
            placeholder="трафік/міс"
            data-testid="donor-traffic"
            style={{ width: 100 }}
            {...donorForm.getInputProps('traffic')}
          />
          <TextInput
            size="xs"
            placeholder="ціна"
            data-testid="donor-price"
            style={{ width: 80 }}
            {...donorForm.getInputProps('price')}
          />
          <TextInput
            size="xs"
            placeholder="USD"
            data-testid="donor-currency"
            style={{ width: 70 }}
            {...donorForm.getInputProps('priceCurrency')}
          />
          <TextInput
            size="xs"
            placeholder="контакт"
            data-testid="donor-contact"
            style={{ width: 160 }}
            {...donorForm.getInputProps('contact')}
          />
          <Select
            size="xs"
            label="Статус"
            allowDeselect={false}
            data={[...DONOR_STATUSES]}
            data-testid="donor-status"
            style={{ width: 130 }}
            {...donorForm.getInputProps('status')}
          />
          <Switch
            size="xs"
            mt={22}
            label="Гемблінг дозволено"
            data-testid="donor-gambling"
            {...donorForm.getInputProps('gamblingAllowed', { type: 'checkbox' })}
          />
        </CreatePanel>

        <DataTable
          rows={donors.data ?? []}
          columns={donorColumns}
          loading={donors.isPending}
          error={donors.error}
          emptyMessage="Донорів немає. Додайте першого — без реєстру розміщення ставлять наосліп."
          onRowClick={(row) => {
            setEditingDonor(row);
          }}
          rowTestId={(row) => `donor-row-${row.domain}`}
          testId="donor-table"
          footerActions={<ExportButton dataset="donors" zone="linkbuilding" label="Експорт донорів" />}
        />

        {editingDonor === null ? null : (
          <DonorEditor
            key={editingDonor.id}
            row={editingDonor}
            onClose={() => {
              setEditingDonor(null);
            }}
          />
        )}
      </Box>

      {siteId === null ? (
        <Box className={classes.panel} data-testid="links-no-site">
          <Text className={classes.dim}>Оберіть сайт.</Text>
        </Box>
      ) : (
        <>
          <Box className={classes.panel} data-testid="links-plan">
            <Text className={classes.panelTitle} mb={8}>
              План
            </Text>
            <Group gap="md" align="flex-end">
              <NumberInput
                label="Бюджет постів"
                size="xs"
                min={0}
                max={100000}
                value={budget}
                onChange={(value) => {
                  setBudget(typeof value === 'number' ? value : 0);
                }}
                data-testid="plan-budget"
                style={{ width: 140 }}
              />
              <NumberInput
                label="Анкорних, %"
                size="xs"
                min={0}
                max={100}
                value={anchorPct}
                onChange={(value) => {
                  setAnchorPct(typeof value === 'number' ? value : 0);
                }}
                data-testid="plan-anchor-pct"
                style={{ width: 140 }}
              />
              <Select
                label="Промпт-профіль (100%)"
                data={(profiles.data?.items ?? []).map((profile) => ({
                  value: profile.id,
                  label: profile.name,
                }))}
                value={profileId}
                onChange={setProfileId}
                size="xs"
                data-testid="plan-profile"
                style={{ width: 220 }}
              />
              <Button
                size="xs"
                disabled={profileId === null}
                loading={savePlan.isPending}
                data-testid="plan-save"
                onClick={() => {
                  if (profileId !== null) {
                    savePlan.mutate({
                      siteId,
                      budgetPosts: budget,
                      anchorPct,
                      promptDistribution: { [profileId]: 100 },
                    });
                  }
                }}
              >
                Зберегти план
              </Button>
            </Group>

            {savePlan.error === null ? null : (
              <Text className={classes.danger} mt={8} data-testid="plan-error">
                {publicMessageOf(savePlan.error)}
              </Text>
            )}

            {plan === undefined ? null : (
              <Group gap="lg" mt="sm">
                <Text className={classes.dim}>
                  бюджет <span className={classes.mono} data-testid="plan-budget-value">{plan.budgetPosts}</span>
                </Text>
                <Text className={classes.dim}>
                  анкорних{' '}
                  <span className={classes.mono} data-testid="plan-anchored">
                    {anchored}
                  </span>{' '}
                  з {plan.budgetPosts}
                </Text>
                <Text className={classes.dim}>
                  лишилось <span className={classes.mono} data-testid="plan-remaining">{plan.remaining}</span>
                </Text>
              </Group>
            )}
          </Box>

          <Box className={classes.panel} data-testid="links-anchors">
            <Text className={classes.panelTitle} mb={8}>
              Анкори
            </Text>
            <Group gap="xs" align="flex-end" mb="sm">
              <TextInput
                label="Текст"
                size="xs"
                value={anchorText}
                onChange={(event) => {
                  setAnchorText(event.currentTarget.value);
                }}
                data-testid="anchor-text"
                style={{ width: 260 }}
              />
              <Select
                label="Тип"
                data={[...ANCHOR_TYPES]}
                value={anchorType}
                onChange={(value) => {
                  setAnchorType(value ?? 'exact');
                }}
                size="xs"
                data-testid="anchor-type"
                style={{ width: 150 }}
              />
              <Button
                size="xs"
                disabled={anchorText.trim() === ''}
                loading={addAnchor.isPending}
                data-testid="anchor-add"
                onClick={() => {
                  addAnchor.mutate({
                    ownerType: 'site',
                    ownerId: siteId,
                    text: anchorText,
                    type: anchorType as (typeof ANCHOR_TYPES)[number],
                  });
                }}
              >
                Додати анкор
              </Button>
            </Group>

            <Group gap="lg" data-testid="anchor-profile">
              {(anchors.data ?? []).map((row) => (
                <Text key={row.type} className={classes.dim} data-testid={`anchor-share-${row.type}`}>
                  {row.type} <span className={classes.mono}>{row.sharePct}%</span> ({row.count} шт · {row.used}{' '}
                  розміщень)
                </Text>
              ))}
            </Group>

            {/*
             * Розподіл за типами анкорів сам по собі не каже, чи сітка йде за планом: сорок
             * анкорів `exact`, якими ще ніхто не скористався, — не перекіс, а беклог.
             * `anchor_pct` рахує, скільки постів мусили б нести анкор; тут — скільки насправді
             * несуть.
             */}
            {plan === undefined ? null : (
              <Text
                className={actualAnchored > anchored ? classes.danger : classes.dim}
                mt={8}
                data-testid="anchor-plan-comparison"
              >
                план анкорних: <span className={classes.mono}>{anchored}</span> з {plan.budgetPosts} (
                {plan.anchorPct}%) · фактично анкорних постів:{' '}
                <span className={classes.mono}>{actualAnchored}</span>
              </Text>
            )}
          </Box>

          <Box className={classes.panel} data-testid="links-posts">
            <Group justify="space-between" mb={8}>
              <Text className={classes.panelTitle}>Гест-пости</Text>
              <Group gap="xs">
                <NumberInput
                  size="xs"
                  min={1}
                  max={100}
                  value={topicCount}
                  onChange={(value) => {
                    setTopicCount(typeof value === 'number' ? value : 1);
                  }}
                  data-testid="topics-count"
                  style={{ width: 90 }}
                />
                <Button
                  size="xs"
                  variant="default"
                  loading={generateTopics.isPending}
                  data-testid="topics-generate"
                  onClick={() => {
                    generateTopics.mutate({ siteId, count: topicCount });
                  }}
                >
                  Згенерувати теми
                </Button>
                <Button
                  size="xs"
                  variant="default"
                  loading={rerunAudit.isPending}
                  data-testid="audit-rerun"
                  onClick={() => {
                    rerunAudit.mutate({ siteId });
                  }}
                >
                  Перепрогнати аудит
                </Button>
              </Group>
            </Group>

            {generateTopics.data === undefined ? null : (
              <Group gap="lg" mb="sm">
                <Text className={classes.dim}>
                  створено <span className={classes.mono} data-testid="topics-created">{generateTopics.data.created}</span>
                </Text>
                <Text className={classes.dim}>
                  дублікатів{' '}
                  <span className={classes.mono} data-testid="topics-duplicates">
                    {generateTopics.data.duplicates.length}
                  </span>
                </Text>
              </Group>
            )}

            {generatePost.error === null ? null : (
              <Text className={classes.danger} mb="sm" data-testid="post-error">
                {publicMessageOf(generatePost.error)}
              </Text>
            )}

            {generatePost.data === undefined ? null : (
              <Text
                className={generatePost.data.written ? classes.dim : classes.danger}
                mb="sm"
                data-testid="post-result"
              >
                {generatePost.data.written
                  ? `записано, ${String(generatePost.data.words)} слів`
                  : `не записано: ${generatePost.data.reason ?? 'перевірка не пройдена'}`}
              </Text>
            )}

            <Table striped withRowBorders={false} verticalSpacing={4} fz="xs">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Тема</Table.Th>
                  <Table.Th>Анкор</Table.Th>
                  <Table.Th>Статус</Table.Th>
                  <Table.Th>Аудит</Table.Th>
                  <Table.Th />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {(posts.data ?? []).map((post) => (
                  <Table.Tr key={post.id} data-testid={`post-${post.id}`}>
                    <Table.Td>{post.topic}</Table.Td>
                    <Table.Td>
                      <Select
                        size="xs"
                        placeholder="без анкора"
                        clearable
                        data={(anchorList.data ?? []).map((anchor) => ({
                          value: anchor.id,
                          label: anchor.text,
                        }))}
                        value={
                          (anchorList.data ?? []).find((anchor) => anchor.text === post.anchorText)?.id ??
                          null
                        }
                        data-testid={`post-anchor-${post.id}`}
                        onChange={(value) => {
                          assignAnchor.mutate({ guestPostId: post.id, anchorId: value });
                        }}
                        style={{ width: 170 }}
                      />
                    </Table.Td>
                    <Table.Td>
                      <Badge size="xs" data-testid={`post-status-${post.id}`}>
                        {post.status}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      {post.auditStatus === null ? (
                        <Text className={classes.dim}>—</Text>
                      ) : (
                        <Badge
                          size="xs"
                          color={AUDIT_COLOR[post.auditStatus] ?? 'graphite'}
                          data-testid={`post-audit-${post.id}`}
                        >
                          {post.auditStatus}
                        </Badge>
                      )}
                    </Table.Td>
                    <Table.Td>
                      <Group gap={6}>
                        <Button
                          size="compact-xs"
                          variant="subtle"
                          data-testid={`post-generate-${post.id}`}
                          onClick={() => {
                            generatePost.mutate({ id: post.id });
                          }}
                        >
                          згенерувати
                        </Button>
                        {/*
                         * Написане досі нікуди не віддавалось: текст лежав у `text_body`, а
                         * прочитати, виправити чи скопіювати донору його не було де.
                         */}
                        <Button
                          size="compact-xs"
                          variant="subtle"
                          data-testid={`post-open-${post.id}`}
                          onClick={() => {
                            setOpenPostId(post.id);
                          }}
                        >
                          відкрити
                        </Button>
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>

            <Group gap="sm" mt="sm">
              {/*
               * «ЕКСПОРТ У РОБОТУ» мокапа: те, що віддають копірайтеру або донору. Експорт
               * донорів живе разом із реєстром донорів вище — той самий testid, одна кнопка на
               * екрані, а не дві з однаковим `data-testid="export-donors"`.
               */}
              <ExportButton dataset="guestPosts" zone="linkbuilding" label="Експорт у роботу" />
              <ExportButton dataset="anchors" zone="linkbuilding" label="Експорт анкорів" />
            </Group>
          </Box>

          {openPostId === null ? null : (
            <GuestPostEditor
              key={openPostId}
              postId={openPostId}
              onClose={() => {
                setOpenPostId(null);
              }}
            />
          )}
        </>
      )}
    </Stack>
  );
}

/**
 * Картка донора — правка статусу й метрик уже заведеного рядка.
 *
 * `upsertDonor` апсертить за доменом, тому домен тут незмінний: дозволити його правити
 * означало б завести новий рядок замість перейменування наявного. Видалення немає — статус
 * `blacklist` і є способом прибрати донора з обігу, лишивши слід для аудиту.
 */
function DonorEditor({ row, onClose }: { row: DonorRow; onClose: () => void }): React.JSX.Element {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const form = useForm<Omit<DonorFormValues, 'domain'>>({
    initialValues: {
      dr: row.dr === null ? '' : String(row.dr),
      traffic: row.traffic === null ? '' : String(row.traffic),
      price: row.price === null ? '' : String(row.price / 100),
      priceCurrency: row.priceCurrency,
      contact: row.contact ?? '',
      gamblingAllowed: row.gamblingAllowed,
      status: row.status,
    },
  });

  const update = useMutation(
    trpc.links.upsertDonor.mutationOptions({
      onSuccess: async () => {
        await queryClient.invalidateQueries({ queryKey: trpc.links.pathKey() });
        onClose();
      },
    }),
  );

  return (
    <RowEditor
      title={row.domain}
      testId="donor"
      zone="linkbuilding"
      onClose={onClose}
      busy={update.isPending}
      error={update.error}
      confirmingDelete={confirmingDelete}
      onConfirmingDeleteChange={setConfirmingDelete}
      onSubmit={form.onSubmit((values) => {
        update.mutate({
          domain: row.domain,
          dr: values.dr.trim() === '' ? null : Number(values.dr),
          traffic: values.traffic.trim() === '' ? null : Number(values.traffic),
          price: values.price.trim() === '' ? null : Math.round(Number(values.price) * 100),
          priceCurrency: values.priceCurrency.trim() || 'USD',
          contact: values.contact.trim() === '' ? null : values.contact.trim(),
          gamblingAllowed: values.gamblingAllowed,
          status: values.status,
        });
      })}
    >
      <TextInput
        size="xs"
        label="Домен"
        disabled
        value={row.domain}
        data-testid="edit-donor-domain"
        style={{ width: 180 }}
      />
      <TextInput
        size="xs"
        label="DR"
        data-testid="edit-donor-dr"
        style={{ width: 70 }}
        {...form.getInputProps('dr')}
      />
      <TextInput
        size="xs"
        label="Трафік"
        data-testid="edit-donor-traffic"
        style={{ width: 100 }}
        {...form.getInputProps('traffic')}
      />
      <TextInput
        size="xs"
        label="Ціна"
        data-testid="edit-donor-price"
        style={{ width: 80 }}
        {...form.getInputProps('price')}
      />
      <TextInput
        size="xs"
        label="Валюта"
        data-testid="edit-donor-currency"
        style={{ width: 70 }}
        {...form.getInputProps('priceCurrency')}
      />
      <TextInput
        size="xs"
        label="Контакт"
        data-testid="edit-donor-contact"
        style={{ width: 160 }}
        {...form.getInputProps('contact')}
      />
      <Select
        size="xs"
        label="Статус"
        allowDeselect={false}
        data={[...DONOR_STATUSES]}
        data-testid="edit-donor-status"
        style={{ width: 130 }}
        {...form.getInputProps('status')}
      />
      <Switch
        size="xs"
        mt={22}
        label="Гемблінг дозволено"
        data-testid="edit-donor-gambling"
        {...form.getInputProps('gamblingAllowed', { type: 'checkbox' })}
      />
    </RowEditor>
  );
}
